import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  type DocumentDraft,
  documentDraftKeys,
  type DocumentDraftStore,
  saveDocumentDrafts,
} from '@/entities/document-draft';
import type { PendingDraftSave } from './documentDraftPersistence';

interface UseDocumentDraftSaveQueueProps {
  patientId: string;
  store: DocumentDraftStore;
}

interface UseDocumentDraftSaveQueueResult {
  getLatestDrafts(): DocumentDraft[];
  enqueueDraftSave(drafts: DocumentDraft[], callbacks?: Omit<PendingDraftSave, 'drafts'>): void;
}

export function useDocumentDraftSaveQueue({
  patientId,
  store,
}: UseDocumentDraftSaveQueueProps): UseDocumentDraftSaveQueueResult {
  const queryClient = useQueryClient();
  const latestDraftsRef = useRef<DocumentDraft[] | null>(null);
  const pendingSaveRef = useRef<PendingDraftSave | null>(null);
  const saveInFlightRef = useRef(false);

  const getLatestDrafts = useCallback(
    () =>
      latestDraftsRef.current ??
      queryClient.getQueryData<DocumentDraft[]>(documentDraftKeys.patient(patientId, store)) ??
      [],
    [patientId, queryClient, store]
  );

  const flushPendingSave = useCallback(async () => {
    if (saveInFlightRef.current) {
      return;
    }
    const pendingSave = pendingSaveRef.current;
    if (pendingSave === null) {
      return;
    }

    pendingSaveRef.current = null;
    saveInFlightRef.current = true;
    try {
      await saveDocumentDrafts(patientId, pendingSave.drafts, store);
      const isLatestSave =
        latestDraftsRef.current === pendingSave.drafts && pendingSaveRef.current === null;
      if (isLatestSave) {
        queryClient.setQueryData(documentDraftKeys.patient(patientId, store), pendingSave.drafts);
        pendingSave.onSuccess?.();
      }
    } catch (error) {
      const isLatestSave =
        latestDraftsRef.current === pendingSave.drafts && pendingSaveRef.current === null;
      if (isLatestSave) {
        pendingSave.onError?.(error);
      }
    } finally {
      saveInFlightRef.current = false;
      if (pendingSaveRef.current !== null) {
        void flushPendingSave();
      }
    }
  }, [patientId, queryClient, store]);

  const enqueueDraftSave = useCallback(
    (drafts: DocumentDraft[], callbacks: Omit<PendingDraftSave, 'drafts'> = {}) => {
      latestDraftsRef.current = drafts;
      pendingSaveRef.current = { drafts, ...callbacks };
      if (!saveInFlightRef.current) {
        void flushPendingSave();
      }
    },
    [flushPendingSave]
  );

  return { getLatestDrafts, enqueueDraftSave };
}
